export default {
  title: "Terms of Service",
  eyebrow: "Legal · Terms",
  updated: "June 2026",
  sections: [
    {
      id: "acceptance",
      label: "Acceptance of terms",
      heading: "1. Acceptance of terms",
      blocks: [
        { p: 'By creating an account, downloading, installing, or using the BeaconShield mobile application (the "App") or visiting beacon-shield.com (the "Site"), you agree to these Terms of Service ("Terms") and our <a href="/privacy">Privacy Policy</a>. If you do not agree, do not use the App or Site. These Terms are an agreement between you and BeaconShield ("BeaconShield", "we", "us"). The same Terms are shown in-app under Settings → Legal.' },
      ],
    },
    {
      id: "emergency",
      label: "Not emergency services",
      heading: "2. Not a substitute for emergency services",
      blocks: [
        { p: "The App displays estimated emergency-room and walk-in clinic wait times and offers an AI assistant to help you think through your options. It is not a medical device, does not provide medical advice or diagnosis, and does not replace professional medical judgment, a nurse line such as 811, or emergency services. Always call 911 or go to the nearest emergency room if you believe you are experiencing a medical emergency. Never delay seeking care because of information shown in the App." },
      ],
    },
    {
      id: "accounts",
      label: "Accounts & eligibility",
      heading: "3. Accounts and eligibility",
      blocks: [
        {
          ul: [
            "You may sign in with Google or register with an email address and password. You must provide accurate information and keep your credentials confidential.",
            "You are responsible for activity on your account. Review your active sessions in “Your devices” and sign out of any you do not recognise.",
            "You must be at least 13 years old (14 in Québec) to create an account. If you are under the age of majority in your province, a parent or guardian must agree to these Terms on your behalf.",
            "Family members you add are labels only (a name) to help you plan; you confirm you have the right to add them.",
          ],
        },
      ],
    },
    {
      id: "testing",
      label: "Internal testing program",
      heading: "4. Internal testing program",
      blocks: [
        { p: "The App is currently available only to invited participants in an internal testing program through Google Play and the Apple App Store. Test builds may be unstable, may change without notice, and may be withdrawn at any time. Features described on the Site may not be available in the current build. Feedback you provide during testing may be used by us without obligation to you." },
      ],
    },
    {
      id: "user-content",
      label: "Your reports, reviews & chats",
      heading: "5. Your reports, reviews, and chats",
      blocks: [
        { p: 'You may submit wait-time reports, reviews, ratings, and messages to the AI assistant ("User Content"). You keep ownership of your User Content. You grant BeaconShield a worldwide, non-exclusive, royalty-free licence to host, display, aggregate, and de-identify User Content in order to operate and improve the App — for example, to show community wait-time estimates and hospital ratings to other users. You agree that your reports and reviews:' },
        {
          ul: [
            "reflect your honest, first-hand experience;",
            "do not contain personal information about other people, including hospital staff or patients;",
            "are not defamatory, abusive, discriminatory, or unlawful; and",
            "are not submitted to manipulate rankings or wait-time estimates.",
          ],
        },
        { p: "We may remove User Content that violates these Terms and may suspend accounts that repeatedly do so." },
      ],
    },
    {
      id: "ai-triage",
      label: "AI triage assistant",
      heading: "6. AI triage assistant",
      blocks: [
        { p: "The AI assistant generates responses using third-party large language models. Its replies are for general informational purposes, may be incomplete or incorrect, and are not a diagnosis or treatment plan. Do not rely on it in an emergency. Chat history is saved so you can refer back to it, and you can delete it at any time in-app. Do not share other people's health information in the chat." },
      ],
    },
    {
      id: "accuracy",
      label: "Wait-time estimates",
      heading: "7. Wait-time estimates",
      blocks: [
        { p: "Wait times shown in the App are estimates derived from user-submitted reports, historical patterns, and, where available, publicly published hospital data. They may be delayed, incomplete, or inaccurate, and actual wait times depend on patient acuity and hospital conditions that we do not control. Hospitals triage by medical need, not by arrival order. Drive-time estimates come from mapping providers and depend on traffic. We make no guarantee that you will be seen within any displayed time or arrive by any displayed ETA." },
      ],
    },
    {
      id: "referrals",
      label: "Referrals, badges & alerts",
      heading: "8. Referrals, badges, and alerts",
      blocks: [
        {
          ul: [
            "Referral codes, streaks, badges, and leaderboards are for recognition only. They have no cash value, cannot be transferred or redeemed for money, and may be modified or discontinued at any time.",
            "Wait alerts and arrival check-ins are conveniences, not guarantees. Alerts may be delayed or fail to deliver.",
            "You may unsubscribe from the weekly impact digest at any time. Security and password-reset emails are essential to the service and are sent while your account is active.",
          ],
        },
      ],
    },
    {
      id: "acceptable-use",
      label: "Acceptable use",
      heading: "9. Acceptable use",
      blocks: [
        { p: "Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable licence to use the App for personal, non-commercial purposes. You agree not to:" },
        {
          ul: [
            "reverse engineer, decompile, or attempt to extract the source code of the App;",
            "scrape, harvest, or redistribute wait-time data, reviews, or other content from the App or Site;",
            "submit false reports, create multiple accounts, or abuse referral codes;",
            "interfere with the operation or security of the App or other users' accounts; or",
            "use the App for any unlawful purpose.",
          ],
        },
      ],
    },
    {
      id: "ip",
      label: "Intellectual property",
      heading: "10. Intellectual property",
      blocks: [
        { p: "The App, Site, and all related content, trademarks, and logos (other than your User Content) are owned by BeaconShield or its licensors and are protected by intellectual-property laws. Nothing in these Terms grants you any right to use our trademarks without prior written consent." },
      ],
    },
    {
      id: "third-party",
      label: "Third-party services",
      heading: "11. Third-party services",
      blocks: [
        { p: 'The App uses Google Sign-In and Google Maps, AI models from OpenAI and Anthropic via a managed service, and Resend for email delivery. Your use of Google Maps within the App is also subject to the <a href="https://maps.google.com/help/terms_maps/" target="_blank" rel="noopener noreferrer">Google Maps/Google Earth Additional Terms of Service</a> and <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a>. We are not responsible for third-party services.' },
      ],
    },
    {
      id: "disclaimers",
      label: "Disclaimers",
      heading: "12. Disclaimers",
      blocks: [
        { p: 'TO THE EXTENT PERMITTED BY APPLICABLE LAW, THE APP AND SITE ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE APP WILL BE UNINTERRUPTED OR ERROR-FREE, OR THAT WAIT-TIME INFORMATION OR AI RESPONSES WILL BE ACCURATE OR CURRENT.' },
      ],
    },
    {
      id: "liability",
      label: "Limitation of liability",
      heading: "13. Limitation of liability",
      blocks: [
        { p: "TO THE MAXIMUM EXTENT PERMITTED BY LAW, BEACONSHIELD AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND SUPPLIERS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, OR GOODWILL, ARISING OUT OF OR RELATED TO YOUR USE OF OR INABILITY TO USE THE APP OR SITE, INCLUDING RELIANCE ON ANY WAIT-TIME ESTIMATE OR AI RESPONSE. OUR TOTAL LIABILITY FOR ANY CLAIM SHALL NOT EXCEED CAD $50." },
        { p: "Some provinces, including Québec, do not allow certain limitations of liability or warranty exclusions for consumers. Nothing in these Terms limits rights you have under applicable consumer-protection law that cannot be waived." },
      ],
    },
    {
      id: "termination",
      label: "Termination",
      heading: "14. Termination",
      blocks: [
        { p: "You may delete your account at any time in-app (Settings → Delete account). We may suspend or terminate your access at any time for violation of these Terms, or at the end of the internal testing program, with notice where reasonably practical. Sections that by their nature should survive termination will survive." },
      ],
    },
    {
      id: "governing-law",
      label: "Governing law",
      heading: "15. Governing law and disputes",
      blocks: [
        { p: "These Terms are governed by the laws of Canada and the province in which BeaconShield is based, without regard to conflict-of-law principles. Subject to any non-waivable consumer rights, disputes will be resolved in the courts of that province." },
      ],
    },
    {
      id: "changes",
      label: "Changes to these terms",
      heading: "16. Changes to these terms",
      blocks: [
        { p: 'We may revise these Terms as the App evolves. The current version will always be posted at this URL and in-app with an updated "Last updated" date. Continued use of the App after changes take effect constitutes acceptance of the revised Terms.' },
      ],
    },
    {
      id: "contact",
      label: "Contact",
      heading: "17. Contact",
      blocks: [{ p: 'Questions about these Terms: <a href="mailto:BeaconShieldApp@gmail.com" data-testid="terms-contact-email">BeaconShieldApp@gmail.com</a>.' }],
    },
  ],
};
